import { useQuery } from '@tanstack/react-query';

import { getLibraryTabs } from '@/requests/library/getLibraryTabs';
import { getAppStore, setAppStore } from '@/store';

import type { LibraryTab } from '@/types';

const useLibraryTabsQuery = () => {
  return useQuery<LibraryTab[] | null>({
    queryKey: ['library-tabs'],
    queryFn: async () => {
      const data = await getLibraryTabs();
      if (!data?.length) return null;

      const { activeTabSlug, shouldScrollToActiveTab } = getAppStore();

      // Don't override activeTabSlug if a deeplink scroll is pending
      // Let ContinuousGallery handle invalid category slugs gracefully
      if (shouldScrollToActiveTab) {
        return data;
      }

      const availableSlugs = new Set(data.map(({ slug }) => slug));
      const fallbackSlug = data[0].slug;
      const nextActiveTabSlug =
        activeTabSlug && availableSlugs.has(activeTabSlug)
          ? activeTabSlug
          : fallbackSlug;

      if (nextActiveTabSlug && activeTabSlug !== nextActiveTabSlug) {
        setAppStore({ activeTabSlug: nextActiveTabSlug });
      }

      return data;
    },
    // Cache tabs for 10 minutes - they rarely change
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

export { useLibraryTabsQuery };
